import React from 'react';
import FormStore from '~/lib/store/FormStore';
import { SshKeyInfo } from '~/lib/store/SshKeysStore';
import { observer } from 'mobx-react';
import { FormFeedback, FormGroup, FormText, Input, Label } from 'reactstrap';
import ErrorList from '../ErrorList';
import { SshKey } from '~/lib/ApiModel';
import { field, is, validate } from '~/lib/helper/ValidationHelper';

export class SshKeyFormStore extends FormStore<SshKeyInfo, keyof SshKeyInfo> {
    constructor(values?: Partial<SshKeyInfo>) {
        super({ ...SshKeyFormStore.defaultValues(), ...(values ?? {}) });
    }

    initFromSshKey(sshKey: SshKey | undefined): void {
        const defaults = SshKeyFormStore.defaultValues();
        this.init({
            name: sshKey?.name ?? defaults.name,
            default: sshKey?.default ?? defaults.default,
            public_key: sshKey?.public_key ?? defaults.public_key,
        });
    }

    static defaultValues(): SshKeyInfo {
        return { name: '', default: false, public_key: '' };
    }

    validateForMode(mode: 'create' | 'edit'): boolean {
        return super.validate((v) =>
            validate(
                v,
                field('name', is.requiredNotEmpty('Name is required')),
                ...(mode === 'create'
                    ? [field<SshKeyInfo, 'public_key'>('public_key', is.requiredNotEmpty('Public key is required'))]
                    : [])
            )
        );
    }
}

type SshKeyFormProps = {
    store: SshKeyFormStore;
    mode: 'create' | 'edit';
};

@observer
export default class SshKeyForm extends React.Component<SshKeyFormProps> {
    render() {
        const form = this.props.store;
        return (
            <>
                <FormGroup>
                    <Label for="sshKeyNameEdit">Name</Label>
                    <Input id="sshKeyNameEdit" type="text" autoFocus {...form.inputProps('name')} />
                    <FormFeedback>
                        <ErrorList errors={form.errorsFor('name')} />
                    </FormFeedback>
                </FormGroup>

                {this.props.mode === 'create' && (
                    <FormGroup>
                        <Label for="sshKeyPublicKeyEdit">Public Key</Label>
                        <Input
                            id="sshKeyPublicKeyEdit"
                            type="textarea"
                            {...form.inputProps('public_key')}
                            className="h-12rem"
                        />
                        <FormText color="muted">Paste your public key in OpenSSH "authorized_keys" format.</FormText>
                        <FormFeedback>
                            <ErrorList errors={form.errorsFor('public_key')} />
                        </FormFeedback>
                    </FormGroup>
                )}

                <FormGroup check>
                    <Input id="sshKeyDefaultCheck" type="checkbox" {...form.checkboxProps('default')} />
                    <Label check for="sshKeyDefaultCheck" type="checkbox">
                        Select this SSH Key for all new Cloud Server Installations
                    </Label>
                </FormGroup>
            </>
        );
    }
}
